import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../middleware/api';
import { useAuth } from '../context/AuthContext';

// Fonction utilitaire pour calculer le prix unitaire en fonction du nombre de mélanges
const calculateUnitPrice = (mixtureCount) => {
  if (mixtureCount >= 20) return 3.00;
  if (mixtureCount >= 10) return 3.50;
  return 4.00;
};

const SelectionOrderContext = createContext();

export const useSelectionOrder = () => {
  return useContext(SelectionOrderContext);
};

export const SelectionOrderProvider = ({ children, selectionId, selectedMixtures = [] }) => {
  const [selection, setSelection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderType, setOrderType] = useState('unitaire'); // 'unitaire' ou 'capsule'
  const { user } = useAuth();

  // Générer un ID de sélection aléatoire
  const generatedSelectionId = selectionId || `${Date.now()}x${Math.floor(Math.random() * 10000000000000000)}`;

  useEffect(() => {
    const fetchSelection = async () => {
      // Si nous avons un selectionId, récupérer la sélection existante
      if (selectionId) {
        try {
          setLoading(true);
          const response = await api.get(`/selections/${selectionId}`);
          if (response.data) {
            setSelection(response.data);
          } else {
            setError(new Error('Sélection non trouvée'));
          }
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      } 
      // Si nous avons des mélanges sélectionnés mais pas de selectionId, créer une sélection à partir des mélanges
      else if (selectedMixtures && selectedMixtures.length > 0) {
        setSelection({
          _id: generatedSelectionId,
          mixtures: selectedMixtures,
          user: user || { firstname: '', lastname: '', email: '' }
        });
        setLoading(false);
      } else {
        // Pas de sélection ni de mélanges
        setSelection({
          _id: generatedSelectionId,
          mixtures: [],
          user: user || { firstname: '', lastname: '', email: '' }
        });
        setLoading(false);
      }
    };

    fetchSelection();
  }, [selectionId, selectedMixtures]);

  const orderUnitaire = async () => {
    try {
      if (!selection || !selection.mixtures || selection.mixtures.length === 0) {
        toast.error('Aucun mélange à commander');
        return false;
      }
      
      const notes = selection.mixtures.map(m => m.name).join(',');
      const unitPrice = calculateUnitPrice(selection.mixtures.length);
      const totalPrice = (selection.mixtures.length * unitPrice).toFixed(2);
      
      const queryParams = new URLSearchParams({
        'add-to-cart': '2329',
        // La quantité est maintenant égale au nombre de mélanges (un testeur par mélange)
        'quantity': selection.mixtures.length.toString(),
        'notes': notes,
        'selectionId': generatedSelectionId,
        'unit-price': unitPrice.toFixed(2),
        'total-price': totalPrice,
        'mixture-count': selection.mixtures.length.toString()
      });
      
      // URL pour commander des testeurs à l'unité
      const orderUrl = `https://www.lumen-care.com/cart/?${queryParams.toString()}`;
      window.open(orderUrl, '_blank');
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la commande unitaire:', error);
      toast.error('Erreur lors de la création de la commande unitaire');
      return false;
    }
  };

  const orderCapsule = async () => {
    try {
      if (!selection || !selection.mixtures || selection.mixtures.length === 0) {
        toast.error('Aucun mélange à commander');
        return false;
      }
      
      const notes = selection.mixtures.map(m => m.name).join(',');
      const queryParams = new URLSearchParams({
        'add-to-cart': '15784',
        'quantity': 1,
        'notes_custom': notes,
        'selectionId': generatedSelectionId
      });
      
      // URL pour commander une capsule sur mesure
      const orderUrl = `https://lumen-care.com/cart/?${queryParams.toString()}`;
      window.open(orderUrl, '_blank');
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la commande de capsule:', error);
      toast.error('Erreur lors de la création de la commande de capsule');
      return false;
    }
  };

  return (
    <SelectionOrderContext.Provider
      value={{
        selection,
        loading,
        error,
        orderType,
        setOrderType,
        orderUnitaire,
        orderCapsule,
        selectionId: generatedSelectionId,
        calculateUnitPrice,
        setSelection
      }}
    >
      {children}
    </SelectionOrderContext.Provider>
  );
};

export default SelectionOrderProvider; 